import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import { MaskedInput, TextInput } from "components/inputs";
import { parse } from "date-fns";
import { throttle } from "lodash";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useUpdateRatedAgeMutation } from "services/rated-age-api";
import type { RatedAgeType } from "types/DataTypes";
import { DATE_FORMAT } from "utils/date-utils";
import { KaboodleToaster } from "components/common";

import { validateAgeField } from "./rated-age-validation";

// Exported for direct unit testing, and parses before falling back to
// null on NaN or empty since value || null would wrongly turn a real
// 0 into null.
export const parseNumericFieldValue = (
  value: string | number | boolean | null | undefined,
  parser: (raw: string) => number,
): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = typeof value === "string" ? parser(value) : Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

type RatedAgeRowFormValues = {
  id: number;
  life_company: string;
  date_received: Date | null;
  rated_age: string;
  unrated_life_expectancy: string;
};

type RatedAgeRowProps = RatedAgeType;

const RatedAgeRow = (ratedAge: RatedAgeRowProps) => {
  const [updateRatedAge] = useUpdateRatedAgeMutation();

  const {
    reset,
    control,
    formState: { errors, dirtyFields },
    setValue,
  } = useForm<RatedAgeRowFormValues>({
    defaultValues: {
      id: ratedAge.id,
      life_company: ratedAge.life_company,
      date_received: ratedAge.date_received
        ? parse(ratedAge.date_received, DATE_FORMAT, new Date())
        : null,
      rated_age: ratedAge.rated_age?.toString() || "",
      unrated_life_expectancy:
        ratedAge.unrated_life_expectancy?.toString() || "",
    },
  });

  const handleFieldSave = useCallback(
    throttle(
      async (
        fieldName: string,
        value: string | number | boolean | null | undefined,
      ) => {
        try {
          const updatedData: Partial<RatedAgeType> = {
            // always include the foreign keys to satisfy backend validation
            msa: ratedAge.msa,
            cost_projection: ratedAge.cost_projection,
            organization: ratedAge.organization,
          };

          if (fieldName === "life_company") {
            const stringValue = String(value || "");

            // validate the life company name
            const validationError = validateAgeField(
              "life_company",
              stringValue,
            );
            if (validationError) {
              KaboodleToaster.error(validationError);
              return;
            }

            updatedData.life_company = stringValue;
          } else if (fieldName === "date_received") {
            updatedData.date_received = value as string;
          } else if (fieldName === "rated_age") {
            // Integer-only, matching the existing age mask below and the
            // backend field, which is an IntegerField.
            const parsedValue = parseNumericFieldValue(value, (raw) =>
              parseInt(raw, 10),
            );

            // validate the rated age
            const validationError = validateAgeField("rated_age", parsedValue);
            if (validationError) {
              KaboodleToaster.error(validationError);
              return;
            }

            updatedData.rated_age = parsedValue;
          } else if (fieldName === "unrated_life_expectancy") {
            const parsedValue = parseNumericFieldValue(value, parseFloat);

            const validationError = validateAgeField(
              "unrated_life_expectancy",
              parsedValue,
            );
            if (validationError) {
              KaboodleToaster.error(validationError);
              return;
            }

            updatedData.unrated_life_expectancy = parsedValue;
          }

          await updateRatedAge({
            id: ratedAge.id,
            data: updatedData,
          }).unwrap();
        } catch (error) {
          console.error("Failed to update rated age:", error);
          KaboodleToaster.error("Failed to update rated age");
        }
      },
      800,
      { leading: true, trailing: true },
    ),
    [
      ratedAge.id,
      ratedAge.msa,
      ratedAge.cost_projection,
      ratedAge.organization,
      updateRatedAge,
    ],
  );

  // only reset when the rated age ID changes (completely new item)
  useEffect(() => {
    reset({
      id: ratedAge.id,
      life_company: ratedAge.life_company,
      date_received: ratedAge.date_received
        ? parse(ratedAge.date_received, DATE_FORMAT, new Date())
        : null,
      rated_age: ratedAge.rated_age?.toString() || "",
      unrated_life_expectancy:
        ratedAge.unrated_life_expectancy?.toString() || "",
    });
  }, [reset, ratedAge.id]);

  // sync server updates for non-dirty fields
  useEffect(() => {
    if (!dirtyFields.life_company) {
      setValue("life_company", ratedAge.life_company, { shouldDirty: false });
    }
    if (!dirtyFields.date_received) {
      const serverDate = ratedAge.date_received
        ? parse(ratedAge.date_received, DATE_FORMAT, new Date())
        : null;
      setValue("date_received", serverDate, { shouldDirty: false });
    }
    if (!dirtyFields.rated_age) {
      setValue("rated_age", ratedAge.rated_age?.toString() || "", {
        shouldDirty: false,
      });
    }
    if (!dirtyFields.unrated_life_expectancy) {
      setValue(
        "unrated_life_expectancy",
        ratedAge.unrated_life_expectancy?.toString() || "",
        { shouldDirty: false },
      );
    }
  }, [ratedAge, setValue, dirtyFields]);

  return (
    <>
      <ControlledInput
        control={control}
        name="life_company"
        onSave={(value) => handleFieldSave("life_company", value)}
        componentType={TextInput}
        errors={errors}
        maxLength={255}
        classNames="input--left-align"
      />

      <ControlledInput
        control={control}
        name="rated_age"
        onSave={(value) => handleFieldSave("rated_age", value)}
        componentType={MaskedInput}
        mask="age"
        errors={errors}
      />

      <ControlledDateInput
        control={control}
        name="date_received"
        label={null}
        onSave={(value) => handleFieldSave("date_received", value)}
        errors={errors}
        allowFutureDates={false}
      />

      <ControlledInput
        control={control}
        name="unrated_life_expectancy"
        onSave={(value) => handleFieldSave("unrated_life_expectancy", value)}
        componentType={MaskedInput}
        mask="age-decimal"
        errors={errors}
      />
    </>
  );
};

export default RatedAgeRow;
